import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store/hook';
import { clearGuestAction } from 'store/guestModeSlice';
import { IGuestModeOptions } from 'models/apiPaths';
import heic2any from 'heic2any';
// @ts-ignore
import { decode } from "tiff"; 
import { axiosGetHashTagSuggestions } from 'services/api';

export const convertWords = (text: string) => {
  const word = text
    .toLowerCase()
    .split(' ')
    .map((s) => s.charAt(0).toUpperCase())
    .join('');
  return word;
};

export const scrollToPageTop = (
  topPosition?: number,
  scrollBehavior?: ScrollBehavior
) => {
  window.scrollTo({
    top: topPosition ?? 0,
    behavior: scrollBehavior ?? 'smooth'
  });
};

export const getHeight = (id: string) => {
  const background = document.getElementById(id);
  const height = (background && Math.max(background.clientHeight)) || 0;
  return height;
};

export const stringToSpaceTrim = (text: string) => {
  return text.trim()?.length > 0;
};

export const blockedUser = (userDetails : any) => {
  const isBlocked = !userDetails?.blocked && !userDetails?.blockedByOther;
  return isBlocked;
};

export const GuestModeRedirection = (guestMode: IGuestModeOptions) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  if (guestMode?.guestMode) {
    if (guestMode?.action !== 'create-account' && guestMode?.action !== 'sign-in' && guestMode?.redirectTo) {
      navigate(guestMode?.redirectTo);
    } else {
      dispatch(clearGuestAction());
    }
  }
};

export const hasDuplicates = (arr: any[]) => new Set(arr).size !== arr.length;

const mentionRegex = /@?\[\s*userid='(.*?)',\s*(?:name|username)='(.*?)'\s*\]/g;
const groupMentionRegex = /@?\[\s*groupid='(.*?)',\s*(?:name|username)='(.*?)'\s*\]/g;
const userGroupMentionRegex = /@?\[\s*(userid|groupid)='([^']+)',\s*(?:name|username)='([^']+)'\s*\]/g;

export const stripMentionsCompletely = (text: string) => {
  return text.replace(mentionRegex, '');
};

export const stripUserGroupMentionsCompletely = (text: string) => {
  return text.replace(userGroupMentionRegex, '');
};

export const getMentionsList = (text: string) => {
  return text.match(mentionRegex) ?? [];
};

export const getGroupMentionsList = (text: string) => {
  return text.match(groupMentionRegex) ?? [];
};

export const getUserGroupMentionsList = (text: string) => {
  return text.match(userGroupMentionRegex) ?? [];
}

export const countMentionCharacters = (text: string) => {
  const mentionMatches = getMentionsList(text);
  return (mentionMatches as string[]).reduce((total, mention) => total + mention.length, 0);
};

export const extractMentionUserIds = (text: string) => {
  const ids = [];
  let match;

  while ((match = mentionRegex.exec(text)) !== null) {
    ids.push(match[1]); // match[1] is the userid
  }

  return ids;
};

const mentionRegexUseridUsername = /@?\[\s*userid='([^']+)',\s*(?:name|username)='([^']+)'\s*\]/g;
export const replaceMentionsWithOnlyUsernamesAfterMaxLimit = (text: string, maxCount: number) => {
  const mentionlist = getMentionsList(text);
  const indexToReplace = text.indexOf(mentionlist[maxCount]);
  const validPart = text.substring(0, indexToReplace);
  const toReplacePart = text.substring(indexToReplace);
  const parts: string[] = [];
  let lastIndex = 0;

  toReplacePart.replace(mentionRegexUseridUsername, (match, userId, username, offset) => {
    parts.push(text.substring(lastIndex, offset));
    parts.push(`${username}`);
    lastIndex = offset + match.length;
    return '';
  });

  parts.push(toReplacePart.substring(lastIndex));
  return validPart.concat(parts.join(''));
};

export const parseMentionMarkupForIdOnly = (text: string | null) => {
  if (!text) return null;
  
  const parts: any[] = [];
  let lastIndex = 0;

  text.replace(mentionRegexUseridUsername, (match, userId, username, offset) => {
    parts.push(text.substring(lastIndex, offset));
    parts.push(`@${userId}`);
    lastIndex = offset + match.length;
    return '';
  });

  parts.push(text.substring(lastIndex));
  return parts;
};

export const parseUserGroupMentionMarkupForIdOnly = (text: string | null) => {
  if (!text) return null;

  const parts: string[] = [];
  let lastIndex = 0;

  const combinedRegex = /(@\[\s*userid='(.*?)',\s*name='(.*?)'\s*\])|(@\[\s*groupid='(.*?)',\s*name='(.*?)'\s*\])/g;

  text.replace(combinedRegex, (match, _uFull, userId, username, _gFull, groupId, groupName, offset) => {
    parts.push(text.substring(lastIndex, offset));

    if (userId) {
      parts.push(`@[userid=${userId}]`);
    } else if (groupId) {
      parts.push(`@[groupid=${groupId}]`);
    }
    lastIndex = offset + match.length;
    return '';
  });

  parts.push(text.substring(lastIndex));
  return parts;
};

export const buildDisplayAndMappingFromMarkup = (markup: string) => {
  const mapping: number[] = [];
  let display = '';
  let lastIndex = 0;
  let markupIndex = 0;

  userGroupMentionRegex.lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = userGroupMentionRegex.exec(markup)) !== null) {
    const mStart = match.index;
    const mEnd = mStart + match[0].length;
    const displayName = match[3];

    if (mStart > lastIndex) {
      const plainSegment = markup.slice(lastIndex, mStart);
      for (let i = 0; i < plainSegment.length; i++) {
        mapping.push(lastIndex + i);
        display += plainSegment[i];
      }
    }

    for (let i = 0; i < displayName.length; i++) {
      mapping.push(mEnd);
      display += displayName[i];
    }

    lastIndex = mEnd;
    markupIndex = mEnd;
  }

  if (lastIndex < markup.length) {
    const plainSegment = markup.slice(lastIndex);
    for (let i = 0; i < plainSegment.length; i++) {
      mapping.push(lastIndex + i);
      display += plainSegment[i];
    }
  }

  mapping.push(markup.length);

  return { display, mapping };
}

export const isAudio = (fileName: string): boolean => {
  return ['.mp3', '.wav', '.m4a', '.aac'].some((ext) =>
    fileName.toLowerCase().endsWith(ext)
  );
};

export async function processImageFile(file: File): Promise<{ convertedFile: File; previewUrl: string }> {
  const fileName = file.name.toLowerCase();

  const readAsDataURL = (blob: Blob): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });

  if (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    fileName.endsWith(".heic") ||
    fileName.endsWith(".heif")
  ) {
    try {
      const result = await heic2any({
        blob: file,
        toType: "image/jpeg",
        quality: 0.9,
      });

      const convertedBlob = Array.isArray(result) ? result[0] : result;
      const convertedFile = new File(
        [convertedBlob],
        file.name.replace(/\.[^/.]+$/, ".jpg"),
        { type: "image/jpeg" }
      );

      const previewUrl = await readAsDataURL(convertedFile);
      return { convertedFile, previewUrl };
    } catch (err) {
      console.error("HEIC conversion failed:", err);
      throw err;
    }
  }

  if (
    file.type === "image/tiff" ||
    fileName.endsWith(".tif") ||
    fileName.endsWith(".tiff")
  ) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const images = decode(new Uint8Array(arrayBuffer));

      if (images.length > 0) {
        const { width, height, data } = images[0];
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext("2d")!;
        const imageData = ctx.createImageData(width, height);
        imageData.data.set(data);
        ctx.putImageData(imageData, 0, 0);

        const blob = await new Promise<Blob | null>((resolve) =>
          canvas.toBlob(resolve, "image/jpeg", 0.9)
        );

        if (!blob) throw new Error("Canvas toBlob() failed");

        const convertedFile = new File(
          [blob],
          file.name.replace(/\.[^/.]+$/, ".jpg"),
          { type: "image/jpeg" }
        );

        const previewUrl = await readAsDataURL(convertedFile);
        return { convertedFile, previewUrl };
      }

      throw new Error("No valid TIFF frames found");
    } catch (err) {
      console.error("TIFF conversion failed:", err);
      throw err;
    }
  }

  const previewUrl = await readAsDataURL(file);
  return { convertedFile: file, previewUrl };
}

export const getFriendStatus = (friendRequested: boolean, friendRequestReceived: boolean) => {
  return friendRequested ? 'Pending' : friendRequestReceived ? 'Accept' : 'Add Friend';
};

export const isHTMLContent = (content: string) => {
  return /<[a-z][\s\S]*>/i.test(content);
};

export const getDomain = (url: string) => {
  try {
    const domain = new URL(url).hostname;
    return domain.replace(/^www\./, '');
  } catch {
    return url;
  }
};

export const fetchHashtags = async (text: string) => {
  const keyword = text.trim().replace(/^#/, '');
    if (keyword === '') return;
      try {
        const response = await axiosGetHashTagSuggestions(keyword);
        return response;
      } catch (error) {
        console.log(error);
      }
}

 export const hasLetter = (text: string) => {
    return /[a-zA-Z]/.test(text);
  };